import { Button, Input, Modal, Select, Table, Space, DatePicker, theme, Tabs, Upload, Form, Spin, message, Typography, Pagination, Checkbox } from 'antd';
import { SearchOutlined, SettingOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { orderColumns } from './constants';
import "./worklist.css";
import axiosInstance from '../../axios';
import { getUserDetails, hisStatusOptions, makePostCall, makeGetCall, hasReportingPermission } from '../../utils/helper';
import ColumnConfigurator from '../../components/PacsAgGrid/ColumnConfigurator';
import { ColumnConfig, ColumnHeaderActions, PinnedColumnsConfig } from '../../components/PacsAgGrid/types';
import { useSites } from '../../hooks/useSites';
import { useAllowedModalities } from '../../hooks/useAllowedModalities';
import { useBodyParts } from '../../hooks/useBodyParts';
import dayjs, { type Dayjs } from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import { debounce, throttle } from 'lodash';
import ViewNotes from '../ManageNodes/ViewNotes';
import OrderInfoPopup from '../OrderInfo';
import PdfViewer from '../../components/PdfViewer';
import ShareStudyModal from '../../components/ShareStudyModal';
import DownloadStudyModal from '../../components/DownloadStudyModal';
import ExportStudyModal from '../../components/ExportStudyModal';
import SplitStudyModal from '../../components/SplitStudyModal';
import TextArea from 'antd/es/input/TextArea';
import TabPane from 'antd/es/tabs/TabPane';
import { useForm } from 'antd/es/form/Form';
// Import shared action handlers
import * as actionHandlers from '../../utils/tableActionHandlers';
import { useWebSocketContext } from '../../context/WebSocketContext';
import { getAutoRefreshEnabled, subscribeAutoRefresh } from '../../utils/autoRefreshPubSub';
import { SavedSearches } from '../orders/constants';
import PacsAgGrid from '../../components/PacsAgGrid';
import { buildLeadingActionColumns, REFERENCE_LAYOUT_MARKER } from '../../utils/sharedTableColumns';
import { mergeShortcuts, shortcutToFilterAndRange, SHORTCUTS_UPDATED_EVENT } from '../../utils/shortcuts';
import QuickDatePresets from '../../components/QuickDatePresets';

const { RangePicker } = DatePicker;

const MyWorklist = ({ StyledTabsComponent }) => {
  const { token } = theme.useToken();
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [modalityAllSelected, setModalityAllSelected] = useState(true);
  const [savedFilters, setSavedFilters] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const userDetails = getUserDetails();
  const [userList, setUserList] = useState([]);
  const [isUserIdle, setIsUserIdle] = useState(false);
  const lastActivityRef = useRef(Date.now());

  // Use dynamic configuration hooks
  const { centers, loading: sitesLoading } = useSites();
  const { modalityOptions, loading: modalitiesLoading } = useAllowedModalities();
  const { bodyPartOptions, loading: bodyPartsLoading } = useBodyParts();
  // Set the default value to [yesterday at 00:00, today at 23:59]
  const today = dayjs();
  const yesterday = dayjs().subtract(1, 'days');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>([
    yesterday.hour(0).minute(0),
    today.hour(23).minute(59)
  ]);
  const [refreshDisabled, setRefreshDisabled] = useState(false);

  const [viewNotesModal, setViewNotesModal] = useState({ visible: false, details: null });
  const [selectedNote, setSelectedNote] = useState({});
  const [noteEditActive, setNoteEditActive] = useState(false);
  const [saveNoteHandler, setSaveNoteHandler] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(getAutoRefreshEnabled());
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>([]);
  const selectedStudyRecords = useMemo(() => {
    if (!selectedRowKeys?.length || !orders?.data?.length) return [];
    const keySet = new Set(selectedRowKeys.map(k => String(k)));
    return (orders.data as any[]).filter((r: any) =>
      keySet.has(String(r?.pacs_order?.pacs_ord_id))
    );
  }, [selectedRowKeys, orders?.data]);

  // Column configuration state
  const [columnOrder, setColumnOrder] = useState<any[]>([]);
  const [columnConfiguratorVisible, setColumnConfiguratorVisible] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>([]);
  const [pinnedColumns, setPinnedColumns] = useState<PinnedColumnsConfig>({ left: [], right: [] });

  // WebSocket context for real-time updates
  const wsContext = useWebSocketContext();

  useEffect(() => {
    const unsubscribe = subscribeAutoRefresh((enabled) => {
      setAutoRefreshEnabled(enabled);
    });

    return () => unsubscribe();
  }, []);

  // Handle priority filter changes
  const handlePriorityChange = (priority) => {
    if (priority === 'all') {
      // Remove po_priority from filters when "All priorities" is selected
      const temp_filters = { ...filters };
      delete temp_filters['po_priority'];
      setFilters(temp_filters);
      setSelectedPriority(null);
    } else {
      // Set specific priority filter
      handleFilterChange('po_priority', priority);
      setSelectedPriority(priority);
    }
  };

  // Additional state for new functionality (matching PACS page)
  const [orderInfoModal, setOrderInfoModal] = useState({});
  const [viewerModal, setViewerModal] = useState({ visible: false, data: {} });
  const [printLoading, setPrintLoading] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedStudyForShare, setSelectedStudyForShare] = useState(null);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [selectedStudyForDownload, setSelectedStudyForDownload] = useState(null);
  const [splitStudyModalVisible, setSplitStudyModalVisible] = useState(false);
  const [selectedStudyForSplit, setSelectedStudyForSplit] = useState(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [selectedStudyForExport, setSelectedStudyForExport] = useState(null);
  const [receiverForm] = useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  // Series selection modal state
  const [seriesSelectionModal, setSeriesSelectionModal] = useState({
    visible: false,
    studyUID: null,
    record: null,
    seriesList: [],
    selectedSeries: [],
    loading: false,
  });

  useEffect(() => {
    // getOrdersList();
    getUsersList();
    getSavedFilters();
    getQuickFilters();
    loadColumnPreferences();
  }, []);

  // Refresh chips when the user edits shortcuts from the profile dialog.
  useEffect(() => {
    const handler = () => getQuickFilters();
    window.addEventListener(SHORTCUTS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SHORTCUTS_UPDATED_EVENT, handler);
  }, []);

  useEffect(() => {
    console.log("Filters changed", filters);
     // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
    }
    debouncedFilter(filters);
  }, [filters]);

  useEffect(() => {
    console.log("date changed", filters);
    // if (Object.keys(filters).length) {
    debouncedFilter(filters);
    // }
  }, [dateRange]);

  useEffect(() => {
      const unsubscribe = subscribeAutoRefresh((enabled) => {
        setAutoRefreshEnabled(enabled);
      });

    return () => unsubscribe();

  }, []);

  // Auto-refresh effect
  useEffect(() => {
    let intervalId;

    if (autoRefreshEnabled && !isUserIdle) {
      intervalId = setInterval(() => {
        // Only refresh if not currently loading and filters are applied
        if (!orders.loading) {
          console.log('Auto-refreshing worklist...');
          // Refresh current page only - maintains user position
          filterResults(filters, pagination.current, pagination.pageSize);
        }
      }, 5 * 60 * 1000); // 5 minutes
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, isUserIdle, orders.loading, filters, dateRange, pagination.current, pagination.pageSize]);


  // Subscribe to WebSocket messages for real-time study updates
  useEffect(() => {
    const unsubscribe = wsContext.subscribe((data) => {
      try {
        // Handle study update notifications using state values directly
        if (data.type === 'study_update' && autoRefreshEnabled && !isUserIdle) {
          console.log('[PACS] Received WebSocket message:', data);
          // Only refresh if not currently loading
          if (!orders.loading) {
            console.log('Auto-refreshing studies list...');
            filterResults(filters, pagination.current, pagination.pageSize);
          }
        }
      } catch (e) {
        console.error('[PACS] Error in WS handler:', e);
      }
    });
      return () => unsubscribe();
  }, [wsContext, autoRefreshEnabled, isUserIdle, orders.loading, filters, pagination]);
  // Idle detection effect
  useEffect(() => {
    const IDLE_TIME = 5 * 60 * 1000; // 5 minutes of inactivity

    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (isUserIdle) {
        setIsUserIdle(false);
      }
    };

    // Throttle the idle timer reset to prevent excessive function calls
    const throttledResetIdleTimer = throttle(resetIdleTimer, 1000, {
      leading: true,
      trailing: false
    });

    const checkIdleStatus = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;

      if (timeSinceActivity >= IDLE_TIME && !isUserIdle) {
        setIsUserIdle(true);
        console.log('User is idle, pausing auto-refresh');
      } else if (timeSinceActivity < IDLE_TIME && isUserIdle) {
        setIsUserIdle(false);
        console.log('User is active, resuming auto-refresh');
      }
    };

    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, throttledResetIdleTimer, true);
    });

    // Check idle status every 30 seconds
    const idleCheckInterval = setInterval(checkIdleStatus, 30 * 1000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledResetIdleTimer, true);
      });
      clearInterval(idleCheckInterval);
    };
  }, [isUserIdle]);

  // const debouncedFilter = debounce(() => { filterResults() }, 300);

  const debouncedFilter = useCallback(
    debounce((tempFilters) => {
      console.log('Debounced value:');
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const getUsersList = () => {
    makePostCall('/user-list', {}).then(res => {
      setUserList(res.data?.data || []);
    })
      .catch(e => {
        console.log(e);
        setUserList([]);
      })
  }

  const getQuickFilters = () => {
    makePostCall('/get-my-shortcuts', { user_id: getUserDetails()?.username })
      .then(res => {
        setQuickFilters(mergeShortcuts(res.data?.data));
      })
      .catch(e => {
        console.log(e);
        setQuickFilters([]);
      });
  }

  const getSavedFilters = () => {
    makePostCall('/get-saved-filters', { user_id: getUserDetails()?.username })
      .then(res => {
        setSavedFilters(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setSavedFilters([]);
      });
  }


  const getOrdersList = async () => {
    makePostCall('/my-worklist', {
      role: userDetails?.user_type,
      user_id: getUserDetails()?.username,
    })
      .then(res => {
        setOrders({ data: res.data?.data || [], loading: false })
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false })
      });
  }

  // Removed modal functionality - everything opens in new tab now

  const filterResults = (tempFilters, page = 1, pageSize = 20) => {
    setOrders({ ...orders, loading: true, data: [] });
    const payload = {
      ...tempFilters,
      role: userDetails?.user_type,
      user_id: getUserDetails()?.username,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize
    };

    if (dateRange?.[0] && dateRange?.[1]) {
      payload['from_date'] = dayjs(dateRange[0]).format('YYYYMMDDHHmmss');
      payload['to_date'] = dayjs(dateRange[1]).format('YYYYMMDDHHmmss');
      payload['po_study_date'] = dateRange;
    }

    makePostCall('/my-worklist', payload)
      .then(res => {
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0
        })
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false, total: 0 })
      });
  }

  const handleFilterChange = (key, value) => {
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    setFilters(temp_filters);
  }

  const refreshScanStatus = () => {
    setRefreshDisabled(true); // Disable the button
    setTimeout(() => {
      setRefreshDisabled(false); // Re-enable the button after 5 seconds
    }, 20000);
    axiosInstance.get('/update-status')
      .then(res => {
        // console.log("res", res);
        // Refresh with current pagination
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        console.log(e);
      })
  }

  const debouncedRefresh = useCallback(
    debounce(() => {
      console.log('Debounced value:');
      refreshScanStatus();
    }, 200),
    []
  );

  const statusOptions = userDetails?.user_type === 'doc' ? [
    { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
  ] :
    [
      { label: 'NEW', value: 'PENDING' },
      { label: 'SCANNED', value: 'SCANNED' },
      { label: 'DRAFTED', value: 'DRAFTED' },
      { label: 'REVIEWED', value: 'REVIEWED' },
      { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
    ]

  // All options now come from dynamic hooks:
  // siteOptions, modalityOptions, bodyPartOptions

  const reportedByOptions = useMemo(() => {
    return userList?.map((user) => ({ label: user.user_fullname, value: user.username }))
  }, [userList]);

  const viewNotes = (rec) => {
    console.log("view notes", rec);
    const firstNote = rec.note_id || {};
    setSelectedNote(firstNote)
    setNoteEditActive(false);
    setViewNotesModal({ visible: true, details: rec })
  }

  const handleNoteSelection = useCallback((note, _record) => {
    setSelectedNote(prevSelectedNote => {
      const previousNoteId = (prevSelectedNote as any)?.rn_id || null;
      const nextNoteId = note?.rn_id || null;
      const previousNoteText = (prevSelectedNote as any)?.rn_notes || '';
      const nextNoteText = note?.rn_notes || '';

      if (previousNoteId === nextNoteId && previousNoteText === nextNoteText) {
        return prevSelectedNote;
      }

      return note || {};
    });
  }, [])

  const clearFilters = () => {
    setFilters({});
    setDateRange(null);
    setModalityAllSelected(true);
    setSelectedPriority(null);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults({}, 1, pagination.pageSize);
  }

  const handleFilterSelection = selectedSavedFilter => {
    const filterString = selectedSavedFilter.uf_filter_json;
    const filterJson = JSON.parse(filterString);

    setFilters(filterJson);
  };

  const handleQuickFilters = selectedFilter => {
    // Compound shortcut: apply the whole filter set + date range at once.
    if (selectedFilter.filterRaw) {
      const { filters: f, dateRange: dr } = shortcutToFilterAndRange(selectedFilter.filterRaw);
      setFilters(f);
      setModalityAllSelected(!(f.modality && f.modality.length));
      if (dr) setDateRange(dr);
      return;
    }

    // LEGACY (pre-compound shortcuts) — kept for context. Quick filters used to
    // be single-key rows: qf_type DATE set the date range, TOGGLE flipped a
    // boolean flag (Emergency/Re-reported), NA set one field=value. Migration
    // 20260618000003 converted all rows to compound qf_filter_json, so every
    // shortcut now takes the `filterRaw` path above and this branch is unused.
    //
    // const { qf_type, qf_date_start, qf_date_end, qf_key, qf_value } = selectedFilter;
    // if (qf_type === 'DATE') {
    //   const toDate = dayjs().subtract(qf_date_end, 'days').hour(23).minute(59).second(0);
    //   const fromDate = dayjs().subtract(qf_date_start, 'days').hour(0).minute(0).second(0);
    //   setDateRange([fromDate, toDate]);
    // } else if (qf_type === 'TOGGLE') {
    //   handleFilterChange(qf_key, !filters[qf_key]);
    // } else {
    //   setFilters({
    //     ...filters,
    //     [qf_key]: qf_key === 'modality' && qf_value && !Array.isArray(qf_value) ? [qf_value] : qf_value,
    //   });
    // }
  };

  // Action handlers using shared utilities
  const openViewer = (record) => actionHandlers.openViewer(record, userDetails);
  const openRadDesk = (record) => actionHandlers.openRadDesk(record, userDetails);
  const openReport = (record) => {
    // Keep click behavior in sync with PACS list: in dual viewer mode,
    // patient-name/report clicks should open the viewer instead of report editor.
    if ((window as any)?.ENV_CONFIG?.DUAL_VIEWER_MODE) {
      return openViewer(record);
    }

    return actionHandlers.openReport(record, userDetails);
  };
  const printReport = (rec) => actionHandlers.printReport(rec, setPrintLoading);
  const downloadDocxReport = (rec) => actionHandlers.downloadDocxReport(rec, setPrintLoading);
  const viewReport = (rec) => actionHandlers.viewReport(rec, setPrintLoading, setViewerModal);
  // Status-column click opens the report in Chrome's native PDF viewer (a
  // separate popup window) rather than the in-app modal.
  const openReportInWindow = (rec) => actionHandlers.openReportInWindow(rec, setPrintLoading);


  const showOrdDetails = (rec) => {
    setOrderInfoModal({ visible: true, data: rec });
  }

  const closeOrdInfo = () => {
    setOrderInfoModal({ visible: false, data: null });
  }

  const handleShareStudy = (record) => {
    setSelectedStudyForShare(record);
    setShareModalVisible(true);
  };

  const handleDownloadStudy = (record) => {
    setSelectedStudyForDownload(record);
    setDownloadModalVisible(true);
  };

  const handleSplitStudy = (record) => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }
    setSelectedStudyForSplit(record);
    setSplitStudyModalVisible(true);
  };

  const handleExportStudy = (record) => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }
    setSelectedStudyForExport(record);
    setExportModalVisible(true);
  };

  // Get DICOM config URL helper
  const getConfigUrl = (urlType) => {
    const config = window.config;
    const dicomwebSource = config?.dataSources?.find(
      (source) => source.sourceName === 'dicomweb'
    );
    return (
      dicomwebSource?.configuration?.[urlType] ||
      `https://dev-api.photonpacs.com/dicom/aets/DCM4CHEE/${urlType === 'wadoUriRoot' ? 'wado' : 'rs'}`
    );
  };

  // Fetch DICOM data helper
  const fetchDicomData = async (url) => {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  };

  // Fetch series for a study
  const fetchSeriesForStudy = async (studyUID) => {
    setSeriesSelectionModal(prev => ({ ...prev, loading: true }));

    try {
      const qidoBase = getConfigUrl('qidoRoot');
      const seriesData = await fetchDicomData(`${qidoBase}/studies/${studyUID}/series`);

      const seriesList = seriesData
        .map((series) => ({
          seriesUID: series['0020000E']?.Value?.[0],
          seriesDescription: series['0008103E']?.Value?.[0] || 'Unknown Series',
          modality: series['00080060']?.Value?.[0] || 'OT',
          seriesNumber: parseInt(series['00200011']?.Value?.[0]) || 0,
          seriesDate: series['00080021']?.Value?.[0],
          bodyPart: series['00180015']?.Value?.[0] || '',
          numInstances: parseInt(series['00201209']?.Value?.[0]) || 0,
        }))
        .filter((s) => s.seriesUID) // Filter out any series without UID
        .sort((a, b) => a.seriesNumber - b.seriesNumber); // Sort by series number

      setSeriesSelectionModal(prev => ({
        ...prev,
        seriesList,
        selectedSeries: seriesList.map((s) => s.seriesUID), // Select all by default
        loading: false,
      }));
    } catch (error) {
      message.error(`Failed to fetch series: ${error.message}`);
      setSeriesSelectionModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle series selection button click
  const handleSelectSeries = (record) => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }

    setSeriesSelectionModal({
      visible: true,
      studyUID: record.ps_study_uid,
      record: record,
      seriesList: [],
      selectedSeries: [],
      loading: false,
    });

    fetchSeriesForStudy(record.ps_study_uid);
  };

  // Toggle series selection
  const toggleSeriesSelection = (seriesUID) => {
    setSeriesSelectionModal(prev => ({
      ...prev,
      selectedSeries: prev.selectedSeries.includes(seriesUID)
        ? prev.selectedSeries.filter((uid) => uid !== seriesUID)
        : [...prev.selectedSeries, seriesUID],
    }));
  };

  // Select/Deselect all series
  const handleSelectAllSeries = () => {
    setSeriesSelectionModal(prev => ({
      ...prev,
      selectedSeries: prev.seriesList.map((s) => s.seriesUID),
    }));
  };

  const handleDeselectAllSeries = () => {
    setSeriesSelectionModal(prev => ({
      ...prev,
      selectedSeries: [],
    }));
  };

  // Open viewer with selected series
  const handleOpenSelectedSeries = async () => {
    const { studyUID, selectedSeries } = seriesSelectionModal;

    if (selectedSeries.length === 0) {
      message.warning('Please select at least one series');
      return;
    }

    // Build viewer URL with series filter
    const seriesUIDs = selectedSeries.join(',');
    const viewerUrl = `/viewer/viewer?StudyInstanceUIDs=${studyUID}&SeriesInstanceUIDs=${seriesUIDs}`;

    // Open in new window
    window.open(viewerUrl, '_blank');

    // Close modal
    setSeriesSelectionModal({
      visible: false,
      studyUID: null,
      record: null,
      seriesList: [],
      selectedSeries: [],
      loading: false,
    });
  };

  const editSignedOffReport = (rec) => {
    window.open(`/oo-report?order_ids=${rec.pacs_order.pacs_ord_id}`, '_blank');
  };

  // --- Column configuration (shared with PACS page, module 'pacs_list') ---

  const getBaseOrderColumns = useCallback(() => {
    // Only the patient-name click handler shapes the data columns now; the action
    // icons live in the leading cluster (buildLeadingActionColumns), not here.
    return orderColumns({
      openReportEditor: openReport,
    });
  }, []);

  const saveColumnPreferences = async (newColumnOrder: any[], newPinnedColumns?: PinnedColumnsConfig) => {
    try {
      const columnKeys = newColumnOrder.map((col: any) => {
        const colId = col.key || col.dataIndex || '';
        const identifier: any = {
          key: col.key || null,
          dataIndex: col.dataIndex || null,
          title: typeof col.title === 'string' ? col.title : null,
        };
        if (typeof col.title !== 'string' && col.title?.props?.children) {
          if (typeof col.title.props.children === 'string') {
            identifier.title = col.title.props.children;
          } else if (Array.isArray(col.title.props.children)) {
            identifier.title = col.title.props.children
              .filter((child: any) => typeof child === 'string')
              .join('');
          }
        }
        if (newPinnedColumns) {
          if (newPinnedColumns.left.includes(colId)) {
            identifier.pinned = 'left';
          } else if (newPinnedColumns.right.includes(colId)) {
            identifier.pinned = 'right';
          }
        }
        return identifier;
      });

      await makePostCall('/save-column-preferences', {
        user_id: getUserDetails()?.username,
        module: 'pacs_list',
        column_order: JSON.stringify(columnKeys),
      });
    } catch (error) {
      console.error('Failed to save worklist column preferences:', error);
    }
  };

  const loadColumnPreferences = async () => {
    try {
      const response = await makeGetCall(
        `/get-column-preferences?user_id=${getUserDetails()?.username}&module=pacs-list`
      );

      const baseColumns = getBaseOrderColumns();

      const savedColumnKeys = response.data?.data?.ucp_column_order
        ? JSON.parse(response.data.data.ucp_column_order)
        : null;

      // Discard saved preferences that predate the reference flex layout.
      const isStalePref =
        Array.isArray(savedColumnKeys) &&
        !savedColumnKeys.some((c: any) => (c.key || c.dataIndex) === REFERENCE_LAYOUT_MARKER);

      if (savedColumnKeys && !isStalePref) {
        const loadedPinned: PinnedColumnsConfig = { left: [], right: [] };
        savedColumnKeys.forEach((savedCol: any) => {
          const colId = savedCol.key || savedCol.dataIndex;
          if (colId && savedCol.pinned === 'left') {
            loadedPinned.left.push(colId);
          } else if (colId && savedCol.pinned === 'right') {
            loadedPinned.right.push(colId);
          }
        });
        setPinnedColumns(loadedPinned);

        const savedVisibleIds = savedColumnKeys.map((col: any) => col.key || col.dataIndex).filter(Boolean);
        setVisibleColumnIds(savedVisibleIds);

        const reorderedColumns: any[] = [];
        savedVisibleIds.forEach((colId: any) => {
          const col = baseColumns.find((c: any) => (c.key || c.dataIndex) === colId);
          if (col) {
            reorderedColumns.push(col);
          }
        });

        setColumnOrder(reorderedColumns);
      } else {
        // No saved prefs (or stale pre-reference-layout prefs) - default order,
        // nothing pinned right (actions are a leading cluster now).
        setPinnedColumns({ left: [], right: [] });
        setColumnOrder([]);
      }
    } catch (error) {
      console.error('Failed to load worklist column preferences:', error);
      setPinnedColumns({ left: [], right: [] });
      setColumnOrder([]);
    }
  };

  // All column configs for the ColumnConfigurator modal
  const allColumnConfigs: ColumnConfig[] = useMemo(() => {
    const baseColumns = getBaseOrderColumns();
    return baseColumns.map((col: any) => {
      let headerName = '';
      if (typeof col.title === 'string') {
        headerName = col.title;
      } else if (col.title?.props?.children) {
        const children = col.title.props.children;
        if (typeof children === 'string') {
          headerName = children;
        } else if (Array.isArray(children)) {
          headerName = children.filter((c: any) => typeof c === 'string').join('');
        }
      }
      return {
        colId: col.key || col.dataIndex || '',
        headerName: headerName || col.key || col.dataIndex || '',
      };
    });
  }, []);

  const handleColumnConfigSave = useCallback((newVisibleColumnIds: string[], newPinnedColumns: PinnedColumnsConfig) => {
    setVisibleColumnIds(newVisibleColumnIds);
    setPinnedColumns(newPinnedColumns);

    const baseColumns = getBaseOrderColumns();
    const reorderedColumns: any[] = [];
    newVisibleColumnIds.forEach((colId: string) => {
      const col = baseColumns.find((c: any) => (c.key || c.dataIndex) === colId);
      if (col) {
        reorderedColumns.push(col);
      }
    });

    setColumnOrder(reorderedColumns);
    saveColumnPreferences(reorderedColumns, newPinnedColumns);
  }, []);

  // Compute the final columns to pass to AgGridTable, respecting visibility, order and pinning
  const configuredColumns = useMemo(() => {
    const baseColumns = columnOrder.length > 0
      ? columnOrder
      : getBaseOrderColumns();

    return baseColumns.map((col: any) => {
      const colId = col.key || col.dataIndex;
      let fixed: string | undefined;
      if (pinnedColumns.left.includes(colId)) {
        fixed = 'left';
      } else if (pinnedColumns.right.includes(colId)) {
        fixed = 'right';
      }
      return { ...col, fixed };
    });
  }, [columnOrder, pinnedColumns]);

  // Gear icon for the column configurator. The Actions column is now a leading
  // (left-pinned) cluster outside the configurable column system, so the gear
  // lives on the Status ("Details") header, present for every role.
  const columnHeaderActions: ColumnHeaderActions = useMemo(() => {
    return {
      status: {
        icon: <SettingOutlined />,
        onClick: () => setColumnConfiguratorVisible(true),
        tooltip: 'Configure Columns',
      },
    };
  }, []);

  // Leading (left-pinned) action columns - the full action cluster (or the
  // technician three-icon set), mirroring the reference RIS layout.
  const leadingColumns = useMemo(
    () =>
      buildLeadingActionColumns({
        openViewer,
        showOrdDetails,
        openRadDesk,
        openReportEditor: openReport,
        viewNotes,
        printReport,
        downloadDocxReport,
        viewReport,
        editSignedOffReport,
        handleShareStudy,
        handleDownloadStudy,
        handleExportStudy,
        handleSelectSeries,
        splitStudy: handleSplitStudy,
        role: userDetails?.user_type,
        pageType: 'pacs',
      }),
    []
  );

  const openSelectedStudies = () => {
    if (!hasReportingPermission(userDetails)) {
      message.info('You do not have the permission to do reporting');
      return;
    }
    const reportable = selectedStudyRecords.filter(
      rec => rec?.order_workflow?.ow_pacs_status !== 'NOT_FOR_REPORTING'
    );
    const blockedCount = selectedStudyRecords.length - reportable.length;
    if (!reportable.length) {
      message.info('Selected studies are marked Not for reporting');
      return;
    }
    if (blockedCount > 0) {
      message.info(`${blockedCount} study(ies) marked Not for reporting were skipped`);
    }
    const orderIds = reportable.map(rec => rec?.pacs_order?.pacs_ord_id).join(',');
    window.open(`/oo-report?order_ids=${orderIds}`, '_blank');
  };

  return (
    <Spin spinning={printLoading}>
    <div>
      <div style={{ padding: '0.75rem 1.25rem 0' }}>
        <div style={{
          background: '#FFFFFF',
          borderRadius: '0.75rem',
          border: '1px solid #E2E8F1',
          padding: '1rem 1.25rem',
          marginBottom: '1rem'
        }}>
          {/* Quick filters + action buttons share one row to save vertical space */}
          <div className="filter-toolbar">
            <div className="filter-toolbar-main">
              <SavedSearches
                savedFilters={savedFilters || []}
                handleFilterSelection={handleFilterSelection}
                quickFilters={quickFilters || []}
                handleQuickFilters={handleQuickFilters}
                currentFilters={filters}
              />
            </div>
            <div className="filter-toolbar-actions">
              {hasReportingPermission(userDetails) && (
                <button
                  className="btn-primary btn-compact"
                  disabled={selectedRowKeys.length === 0}
                  onClick={openSelectedStudies}
                >
                  Open Studies{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
                </button>
              )}
              <button
                className="btn-secondary btn-compact"
                onClick={() => { clearFilters() }}
              >
                Clear Search
              </button>
              <button
                className="btn-primary btn-compact"
                onClick={() => filterResults(filters)}
              >
                <SearchOutlined />
                Search Worklist
              </button>
            </div>
          </div>

          <div className="filter-layout">
            <div className="wl-filters-row filter-layout-inputs">
              <div className="wl-field wl-field--name">
                <FyzksInput
                  size="small"
                  placeholder="Patient Name"
                  value={filters['pat_name'] || ''}
                  onChange={(e) => handleFilterChange('pat_name', e.target.value)}
                />
              </div>

              <div className="wl-field wl-field--status">
                <Select
                  size="small"
                  mode="multiple"
                  allowClear
                  placeholder="Status"
                  value={filters['po_status']}
                  style={{ width: '100%' }}
                  options={statusOptions}
                  onChange={(val) => handleFilterChange('po_status', val)}
                />
              </div>

              <div className="wl-field wl-field--body">
                <FyzksInput
                  size="small"
                  placeholder="Body Part / Study Desc"
                  value={filters['po_body_part'] || ''}
                  onChange={(e) => handleFilterChange('po_body_part', e.target.value)}
                />
              </div>

              <div className="wl-field wl-field--site">
                <Select
                  size="small"
                  allowClear
                  placeholder="Site"
                  value={filters['po_site']}
                  style={{ width: '100%' }}
                  options={centers}
                  onChange={(val) => handleFilterChange('po_site', val)}
                />
              </div>

              <div className="wl-field wl-field--ordno">
                <FyzksInput
                  size="small"
                  placeholder="Order No"
                  value={filters['po_ord_no'] || ''}
                  onChange={(e) => handleFilterChange('po_ord_no', e.target.value)}
                />
              </div>

              <div className="wl-field wl-field--hisstatus">
                <Select
                  size="small"
                  mode="multiple"
                  allowClear
                  placeholder="HIS Status"
                  value={filters['po_his_status']}
                  style={{ width: '100%' }}
                  options={hisStatusOptions}
                  onChange={(val) => handleFilterChange('po_his_status', val)}
                />
              </div>

              <div className="wl-field wl-field--refdoc">
                <FyzksInput
                  size="small"
                  allowClear
                  placeholder="Ref Doc."
                  value={filters['po_ref_doc'] || ''}
                  onChange={(e) => handleFilterChange('po_ref_doc', e.target.value)}
                />
              </div>

              <div className="wl-field wl-field--date">
                <div className="date-with-presets">
                  <RangePicker
                    size="small"
                    placeholder={['Study Date', '']}
                    showTime={{
                      format: 'HH:mm',
                      use12Hours: false,
                      defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                    }}
                    format="YYYY-MM-DD HH:mm"
                    value={dateRange}
                    onChange={(val) => {
                      if (val) {
                        setDateRange([val[0], val[1]]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                  />
                  <QuickDatePresets value={dateRange} onChange={setDateRange} />
                </div>
              </div>
            </div>

            <div className="filter-layout-modalities">
              <div className="filter-modalities-header">
                <span className="filter-modalities-label">Modality</span>
                <Checkbox
                  className="filter-modalities-selectall"
                  checked={modalityAllSelected}
                  indeterminate={!modalityAllSelected && (filters['modality']?.length > 0)}
                  onChange={e => {
                    setModalityAllSelected(e.target.checked);
                    handleFilterChange('modality', []);
                  }}
                >
                  Select all
                </Checkbox>
              </div>
              <Checkbox.Group
                value={modalityAllSelected ? modalityOptions.map(o => o.value) : (filters['modality'] || [])}
                onChange={(vals) => {
                  const allChecked = vals.length === modalityOptions.length;
                  setModalityAllSelected(allChecked);
                  handleFilterChange('modality', allChecked ? [] : vals);
                }}
              >
                {modalityOptions.map(opt => (
                  <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
                ))}
              </Checkbox.Group>
            </div>
          </div>
      </div>
      <div className='orders-list'>
        <PacsAgGrid
          height="calc(100vh - 18.5rem)"
          loading={orders.loading}
          rowData={orders.data || []}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={(keys) => setSelectedRowKeys(keys)}
          onRowClick={openViewer}
          onOpenReport={openReport}
          onViewReport={openReportInWindow}
          columns={configuredColumns}
          leadingColumns={leadingColumns}
          columnHeaderActions={columnHeaderActions}
        />
        {/* External pagination for server-side paging */}
        <div className="pacs-ag-grid-pagination">
          <Pagination
            size="small"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={orders?.total || 0}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            onChange={(page, pageSize) => {
              setPagination({ current: page, pageSize });
              setSelectedRowKeys([]);
              filterResults(filters, page, pageSize);
            }}
            showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
          />
        </div>

        {/* Column Configurator Modal */}
        <ColumnConfigurator
          visible={columnConfiguratorVisible}
          onClose={() => setColumnConfiguratorVisible(false)}
          allColumns={allColumnConfigs}
          visibleColumnIds={visibleColumnIds}
          pinnedColumns={pinnedColumns}
          onSave={handleColumnConfigSave}
        />

        {
          viewNotesModal && viewNotesModal.visible && (
            <Modal
              open={viewNotesModal.visible}
              onCancel={() => {
                if (noteEditActive) {
                  setNoteEditActive(false);
                  return;
                }

                setViewNotesModal(null);
                setNoteEditActive(false);
              }}
              onOk={async () => {
                if (saveNoteHandler) {
                  const saved = await saveNoteHandler();
                  if (!saved) {
                    return;
                  }
                }
              }}
              style={{ width: '100%', height: '100%' }}
              width={'90%'}
              okButtonProps={{
                style: {
                  marginTop: '20px',
                },
                disabled: !noteEditActive,
              }}
              okText="Save"
              cancelText={noteEditActive ? 'Cancel' : 'Close'}
            >
              <ViewNotes
                handleNoteSelection={handleNoteSelection}
                selectedNote={selectedNote}
                viewNotesModal={viewNotesModal}
                isEditMode={noteEditActive}
                onEditNoteStateChange={setNoteEditActive}
                onSaveNoteRegister={setSaveNoteHandler}
              />
            </Modal>
          )
        }



        {/* Order Info Modal */}
        {
          orderInfoModal && orderInfoModal.visible && (
            <Modal
              open={orderInfoModal.visible}
              onCancel={closeOrdInfo}
              footer={[
                <Button key="cancel" onClick={closeOrdInfo}>
                  Cancel
                </Button>
              ]}
              width={700}
            >
              <OrderInfoPopup
                data={orderInfoModal.data}
                closeModal={closeOrdInfo}
                refreshTable={() => filterResults(filters)}
              />
            </Modal>
          )
        }

        {/* PDF Viewer Modal */}
        {
          viewerModal && (
            <Modal
              width={1000}
              open={viewerModal?.visible}
              onCancel={() => { setViewerModal({ visible: false }) }}
              okButtonProps={{ style: { display: 'none' } }}
              style={{ top: 20 }}
              styles={{
                body: {
                  height: '85vh',
                  maxHeight: '85vh',
                  overflow: 'hidden',
                  padding: 0
                }
              }}
            >
              <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
                <PdfViewer
                  order_id={viewerModal?.order_id}
                  patient_id={viewerModal?.patient_id}
                  pdfArrayBuffer={viewerModal?.data}
                  patientData={viewerModal?.patientData}
                />
              </div>
            </Modal>
          )
        }

        {/* Share Study Modal */}
        {selectedStudyForShare && (
          <ShareStudyModal
            visible={shareModalVisible}
            onCancel={() => {
              setShareModalVisible(false);
              setSelectedStudyForShare(null);
            }}
            studyUID={selectedStudyForShare?.ps_study_uid}
            studyDescription={selectedStudyForShare?.pacs_order?.po_diag_desc}
            patientName={selectedStudyForShare?.pacs_order?.patient?.pat_name}
            patientGender={selectedStudyForShare?.pacs_order?.patient?.pat_sex}
            patientDOB={selectedStudyForShare?.pacs_order?.patient?.pat_dob}
            studyDate={selectedStudyForShare?.ps_study_dt_tm}
          />
        )}

        {/* Download Study Modal */}
        {selectedStudyForDownload && (
          <DownloadStudyModal
            visible={downloadModalVisible}
            onCancel={() => {
              setDownloadModalVisible(false);
              setSelectedStudyForDownload(null);
            }}
            studyUID={selectedStudyForDownload?.ps_study_uid}
            studyDescription={selectedStudyForDownload?.pacs_order?.po_diag_desc}
            patientName={selectedStudyForDownload?.pacs_order?.patient?.pat_name}
            accessionNumber={selectedStudyForDownload?.pacs_order?.po_acc_no}
            studyData={selectedStudyForDownload}
          />
        )}

        {/* Split Study Modal */}
        {selectedStudyForSplit && (
          <SplitStudyModal
            visible={splitStudyModalVisible}
            record={selectedStudyForSplit}
            onCancel={() => {
              setSplitStudyModalVisible(false);
              setSelectedStudyForSplit(null);
            }}
            onSuccess={() => {
              // Refresh so the newly-created split order appears in the list.
              filterResults(filters);
            }}
          />
        )}

        {/* Export Study Modal */}
        {selectedStudyForExport && (
          <ExportStudyModal
            visible={exportModalVisible}
            studyUID={selectedStudyForExport?.ps_study_uid}
            onCancel={() => {
              setExportModalVisible(false);
              setSelectedStudyForExport(null);
            }}
            onSuccess={() => {
              filterResults(filters);
            }}
          />
        )}

        {/* Series Selection Modal */}
        <Modal
          title="Select Series to Open"
          open={seriesSelectionModal.visible}
          onCancel={() => setSeriesSelectionModal({
            visible: false,
            studyUID: null,
            record: null,
            seriesList: [],
            selectedSeries: [],
            loading: false,
          })}
          footer={[
            <Button
              key="select-all"
              onClick={handleSelectAllSeries}
              disabled={seriesSelectionModal.loading}
            >
              Select All
            </Button>,
            <Button
              key="deselect-all"
              onClick={handleDeselectAllSeries}
              disabled={seriesSelectionModal.loading}
            >
              Deselect All
            </Button>,
            <Button
              key="cancel"
              onClick={() => setSeriesSelectionModal({
                visible: false,
                studyUID: null,
                record: null,
                seriesList: [],
                selectedSeries: [],
                loading: false,
              })}
            >
              Cancel
            </Button>,
            <Button
              key="open"
              type="primary"
              disabled={seriesSelectionModal.selectedSeries.length === 0 || seriesSelectionModal.loading}
              onClick={handleOpenSelectedSeries}
            >
              Open ({seriesSelectionModal.selectedSeries.length} series)
            </Button>,
          ]}
          width={900}
          style={{ top: 40 }}
          bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
        >
          <Table
            dataSource={seriesSelectionModal.seriesList}
            loading={seriesSelectionModal.loading}
            rowKey="seriesUID"
            pagination={false}
            scroll={{ y: 450 }}
            size="middle"
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: seriesSelectionModal.selectedSeries,
              onChange: (selectedRowKeys) => {
                setSeriesSelectionModal(prev => ({
                  ...prev,
                  selectedSeries: selectedRowKeys,
                }));
              },
            }}
            onRow={(record) => ({
              onClick: () => toggleSeriesSelection(record.seriesUID),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: 'Series No',
                dataIndex: 'seriesNumber',
                key: 'seriesNumber',
                width: 100,
                align: 'center',
                render: (num) => <Typography.Text strong>{num}</Typography.Text>,
              },
              {
                title: 'Description',
                dataIndex: 'seriesDescription',
                key: 'seriesDescription',
                ellipsis: true,
                render: (text) => text || 'No description',
              },
              {
                title: 'Body Part',
                dataIndex: 'bodyPart',
                key: 'bodyPart',
                width: 150,
                align: 'center',
                render: (text) => text || '-',
              },
              {
                title: 'Images',
                dataIndex: 'numInstances',
                key: 'numInstances',
                width: 100,
                align: 'center',
                render: (num) => (
                  <Typography.Text type={num === 0 ? 'secondary' : undefined}>
                    {num > 0 ? num : '0'}
                  </Typography.Text>
                ),
              },
            ]}
            locale={{
              emptyText: 'No series found for this study',
            }}
          />
        </Modal>

      </div>
      </div>
    </div>
    </Spin>
  );
}

export default MyWorklist;
